import {
  Box,
  Flex,
  HStack,
  Image,
  Text,
  VStack,
  useDisclosure,
} from "@chakra-ui/react";
import { StyledDashboardLayoutChildContainer } from "@root/components/common/style";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import { DashboardHeader } from "@root/components/dashboard";
import ChatContainerBottomSide from "./chatContainerBottomSide";
import { useMainChatContext } from "@root/context/kps-chat-context";
import React, { useEffect, useMemo, useRef, useState } from "react";
import ChatRightPanel from "./ChatRightPanel";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { useProjectsContext } from "@root/context/projects-context";
import DocumentHeader from "./Checklist-header";
import {
  LAW_INFO_CENTER_MODEL,
  LEGAL_CHATMODE,
  ROUTE_NAMES,
  WEB_SEARCH_MODEL,
} from "@root/constants";
import DrawingPdfViewer from "./DrawingPdfViewer";
import { useDispatch } from "react-redux";
import KTLogo from "@assets/icons/kt-logo.png";
import { useTranslation } from "react-i18next";
import SuggestedQuestionsAndFaqs from "@root/components/suggested-questions-faqs/SuggestedQuestionsAndFaqs";
import useProjectsDashboard from "@root/hooks/useProjectsDashboard";
import { getChatType, GetChatbotName, handleStartChat, safeJsonParse } from "@root/utils";
import { CommonButton } from "@root/components/common/button/commonButton";
import { DownloadIcon } from "@root/assets/icons/downloadIcon";
import RefreshIcon from "@root/assets/icons/history-refresh-icon";
import PlusIcon from "@root/assets/icons/plusicon";
import { ChatHistoryDrawer } from "@root/components/chat-history/chat-history-drawer";
import { BrandedLogo } from "@root/components/branding";
import ModelDisplay from "./model-selection-toggler.tsx/model-text";
import ChatContent from "@root/components/dashboard/ChatContent";

export const MainChatContainer = () => {
  const { t } = useTranslation("common");
  const {
    submitHandler,
    inputRef,
    thraedUID,
    uid,
    chatHeadersData,
    onChangehandler,
    input,
    loading,
    currentThread,
    fileContainerRef,
    setInput,
    riskCheckerInput,
    setRiskCheckerInput,
    riskCheckerLoading,
    riskCheckerInputRef,
    onRiskcheckSelectFile,
    resetData,
    setCurrentPathname,
    setPdfPageNumber,
    documentsData,
    attachedFile,
    setAttachedFile,
    selectedProject,
    setSelectedProject,
    currentMessageResponse,
    showWelcomeText,
    setShowWelcomeText,
    setPrevThread,
    fetchSuggestedQuestions,
    fetchFaqs,
    downloadPdfOfThread,
    downloadPdfLoading,
    openProjectsListing,
    sourcesData,
    legalChatMode,
    setLegalMode,
    setModel,
  } = useMainChatContext();
  const { pathname, state } = useLocation();
  const navigate = useNavigate();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const isProjectChatbot =
    pathname === ROUTE_NAMES.DOCUMENT_INTEGRATION_CHATBOT ||
    pathname.includes("/document-chat");
  const {
    projectsData,
    fetchProjects,
    fetchDocument,
    documentData,
    setDocumentData,
    documentsData: documentsDataFromProjects,
    fetchAdminFile,
  } = useProjectsContext();
  const { onSubmitHandler } = useProjectsDashboard();
  const dispatch = useDispatch();
  const isMultiDoc = pathname.includes("checklist");
  const isKCSChat = pathname.includes(ROUTE_NAMES.KCS);
  const isDrawingAnalysis = pathname.includes(
    ROUTE_NAMES.DRAWING_ANALYSIS_CHAT
  );
  const isLegalChat = pathname.startsWith(ROUTE_NAMES.KSCE_CHAT_SCREEN);
  const completedDocs = documentsData?.rows?.filter(
    (doc: any) => doc?.status === 8
  );
  const { id } = useParams();
  const showFooter = uid || thraedUID;

  const payload = useMemo(() => {
    if (currentThread?.[0]?.meta?.c?.payload) {
      try {
        return JSON.parse(currentThread?.[0]?.meta?.c?.payload);
      } catch {
        return null;
      }
    }
  }, [currentThread?.[0]?.meta?.c?.payload]);

  const lastPayload = useMemo(() => {
    if (currentThread && Array.isArray(currentThread)) {
      const userItems = currentThread.filter(
        (item: any) => item?.role === "user"
      );
      if (userItems.length > 0) {
        const lastUserItem = userItems[userItems.length - 1];
        if (lastUserItem?.meta?.c?.payload) {
          try {
            return JSON.parse(lastUserItem.meta.c.payload);
          } catch {
            return null;
          }
        }
      }
    }
    return null;
  }, [currentThread]);

  const hasChecklistConditionRunRef = useRef(false);

  useEffect(() => {
    if (isDrawingAnalysis && !documentData) {
      if (state?.file) {
        fetchDocument(state?.file?.id);
      } else if (payload) {
        setPdfPageNumber(payload?.page_no);
        fetchDocument(payload?.pdf_id);
      }
    } else if (
      pathname.includes(ROUTE_NAMES.CHECKLIST) &&
      lastPayload &&
      !attachedFile?.length &&
      !hasChecklistConditionRunRef.current
    ) {
      if (lastPayload?.file_id) {
        try {
          const fileIdsArray = safeJsonParse(lastPayload?.file_id);
          if (Array.isArray(fileIdsArray) && fileIdsArray.length > 0) {
            Promise.all(
              fileIdsArray.map((id: number) => fetchDocument(Number(id)))
            )
              .then((responses: any[]) => {
                const documents = responses
                  .map((res) => res?.data?.data)
                  .filter(Boolean);
                if (documents.length > 0) {
                  setAttachedFile(documents);
                }
              })
              .catch(() => {
                // Handle error gracefully
              });
          }
        } catch {
          // Handle JSON parse error gracefully
        }
      }
      hasChecklistConditionRunRef.current = true;
    } else if (pathname.startsWith("/document-chat/")) {
      if (!payload?.project_id) {
        setSelectedProject(null);
      } else {
        setSelectedProject(payload?.project_id);
      }
      if (lastPayload?.file_id) {
        const fileId = Number(lastPayload?.file_id);
        if (fileId) {
          fetchAdminFile(fileId).then((res: any) => {
            if (res?.data?.data) {
              setAttachedFile([res.data.data]);
            }
          })
        }
      }
    } else if (pathname.startsWith(ROUTE_NAMES.KSCE_CHAT_SCREEN) && payload) {
      if (Number(payload?.source) === LEGAL_CHATMODE.WEB_SEARCH) {
        setLegalMode(LEGAL_CHATMODE.WEB_SEARCH);
        if (Number(payload?.model) === WEB_SEARCH_MODEL.CLAUDE_SONNET) {
          setModel(WEB_SEARCH_MODEL.CLAUDE_SONNET);
        } else if (Number(payload?.model) === WEB_SEARCH_MODEL.MIDM) {
          setModel(WEB_SEARCH_MODEL.MIDM);
        }
      } else if (Number(payload?.source) === LEGAL_CHATMODE.LAW_INFO_CENTER) {
        setLegalMode(LEGAL_CHATMODE.LAW_INFO_CENTER);
        if (Number(payload?.model) === LAW_INFO_CENTER_MODEL.CLAUDE_SONNET) {
          setModel(LAW_INFO_CENTER_MODEL.CLAUDE_SONNET);
        } else if (
          Number(payload?.model) === LAW_INFO_CENTER_MODEL.CLAUDE_OPUS
        ) {
          setModel(LAW_INFO_CENTER_MODEL.CLAUDE_OPUS);
        }
      }
    }
  }, [state, payload, lastPayload]);

  useEffect(() => {
    if (!projectsData?.rows?.length) {
      fetchProjects();
    }
    return () => {
      resetData();
      setDocumentData(null);
      setPrevThread(null);
    };
  }, []);

  useEffect(() => {
    setCurrentPathname(pathname);
    if (pathname.includes(ROUTE_NAMES.DRAWING_ANALYSIS_CHAT)) {
      setShowWelcomeText(true);
    }
  }, [pathname]);

  const chatType = useMemo(() => {
    return getChatType(pathname);
  }, [pathname]);

  const isLawInfoCentre = legalChatMode === LEGAL_CHATMODE.LAW_INFO_CENTER;

  const chatbotName = useMemo(() => {
    if (isLegalChat) {
      if (isLawInfoCentre) {
        return t("translate.infoTitle");
      }
      return t("translate.webTitle");
    }
    if (isDrawingAnalysis) {
      return t("sidebar.drawingAnalysisChatbot");
    }
    if (isProjectChatbot) {
      return t("sidebar.projectDocumentChatbot");
    }
    let mode: string | undefined;
    if (chatType === 1 && payload?.source) {
      mode = payload.source === "law_case" ? "search" : "chat";
    }
    return GetChatbotName(chatType, t, mode);
  }, [chatType, payload?.source, t, legalChatMode]);

  const chatbotDescription = useMemo(() => {
    if (isDrawingAnalysis) {
      return t("chatHistory.drawingAnalysisChatbotDescDefaultScreen");
    }
    if (isLegalChat) {
      return t("translate.legalChatbotDescription");
    }
    if (isProjectChatbot) {
      return t("translate.projectDocumenttext");
    }
    return "";
  }, [isDrawingAnalysis]);

  useEffect(() => {
    fetchSuggestedQuestions({
      type: chatType,
    });
    fetchFaqs({
      type: chatType,
    });
  }, [chatType]);

  const getProjectId = () => {
    if (isProjectChatbot && !selectedProject) {
      //if no selected project means integrated mode
      return 3;
    } else if (isProjectChatbot && selectedProject) {
      //if selected project then project mode
      return selectedProject;
    }
    return undefined; // fallback if none of the above
  };

  const renderHeader = () => {
    if (isDrawingAnalysis) {
      return null;
    }
    if (isMultiDoc) {
      return <DocumentHeader />;
    }
    return (
      <Box>
        <DashboardHeader
          copyRef={currentThread}
          hideBtn={uid || thraedUID}
          data={chatHeadersData}
          payload={payload}
        />
      </Box>
    );
  };

  const showDefaultWelcomeText =
    !id && !currentMessageResponse && showWelcomeText;

  const renderDefaultWelcomeScreen = () => {
    return (
      <>
        <Flex flexDir={"column"} alignItems={"center"} gap={"10px"}>
          <BrandedLogo />
          <Text
            fontWeight={700}
            fontSize={"28px"}
            color={"#1A1A1A"}
            textAlign={"center"}
          >
            {chatbotName}
          </Text>
          <Text
            color={"#404040"}
            fontSize={"14px"}
            fontWeight={400}
            maxWidth={"400px"}
            textAlign={"center"}
          >
            {chatbotDescription}
          </Text>
          {/* {!isKCSChat && (
              <SuggestedQuestionsAndFaqs onSubmitHandler={onSubmitHandler} />
            )} */}
        </Flex>
      </>
    );
  };

  const renderChatContent = () => {
    const isPanelVisible = openProjectsListing || sourcesData;
    return (
      <Box position={"relative"} flex={1} overflow={"auto"}>
        <Box
          display={showDefaultWelcomeText ? "block" : "none"}
          position={"absolute"}
          top={"20px"}
          bottom={"20px"}
          left={"20px"}
          right={"20px"}
        >
          <Flex
            alignItems={"center"}
            justifyContent={"center"}
            width={"100%"}
            className="flex-container"
            height={"100%"}
          >
            {renderDefaultWelcomeScreen()}
          </Flex>
        </Box>
        <ChatContent
          data={currentThread}
          showdefaultwelcometext={showDefaultWelcomeText}
          isRightPanelVisible={isPanelVisible}
        />
      </Box>
    );
  };

  const renderMainLayout = () => {
    const isPanelVisible = openProjectsListing || sourcesData;

    return (
      <Box
        height={"100%"}
        width={"100%"}
        position="relative"
        pr={isPanelVisible ? "400px" : "0"}
        transition="padding-right 0.3s ease"
      >
        <StyledDashboardLayoutChildContainer
          className={"mainChat"}
          position={"relative"}
          height={"100%"}
          overflow={"hidden"}
        >
          {renderHeader()}
          {renderChatContent()}
          <ChatContainerBottomSide
            fileContainerRef={fileContainerRef}
            submitHandler={submitHandler}
            inputRef={inputRef}
            onChangehandler={onChangehandler}
            input={input}
            onSubmit={submitHandler}
            loading={loading}
            setInput={setInput}
            hideInputs={showFooter}
            riskCheckerInput={riskCheckerInput}
            setRiskCheckerInput={setRiskCheckerInput}
            riskCheckerInputRef={riskCheckerInputRef}
            onRiskcheckSelectFile={onRiskcheckSelectFile}
            riskCheckerLoading={riskCheckerLoading}
            width={isPanelVisible ? "100%" : "1100px"}
          />
        </StyledDashboardLayoutChildContainer>
        <ChatRightPanel />
      </Box>
    );
  };

  const renderDrawingAnalysisLayout = () => {
    return (
      <VStack height={"100%"} flexDir={"column"} spacing={0}>
        {/* HEADER AREA */}
        <HStack
          borderBottom={"1px solid #CACACA"}
          width={"100%"}
          p={"15px 30px"}
          justifyContent={"space-between"}
          bgColor={"white"}
        >
          <Box
            as="button"
            display="flex"
            alignItems="center"
            gap="8px"
            color="#1A1A1A"
            fontSize="16px"
            fontWeight={400}
            bg="transparent"
            border="none"
            _hover={{
              opacity: 0.7,
            }}
            transition="opacity 0.2s"
          >
            <Text as="span">←</Text>
            <Text
              as="span"
              fontWeight={500}
              color={"#0E0E0E"}
              cursor="pointer"
              onClick={() => navigate(ROUTE_NAMES.DRAWING_ANALYSIS_DASHBOARD)}
            >
              {t("translate.goBack")}
            </Text>
            <Box cursor="default">
              <ModelDisplay modelKey={chatType} />
            </Box>
          </Box>
          <Flex gap={"25px"}>
            {id && (
              <CommonButton
                variant="secondary"
                startIcon={<DownloadIcon />}
                onClick={() => downloadPdfOfThread({ threadId: id })}
                isLoading={downloadPdfLoading}
              >
                {t("translate.downloadAnswer")}
              </CommonButton>
            )}

            {id && (
              <CommonButton
                variant="primary"
                startIcon={
                  <PlusIcon width="18px" height="18px" color="white" />
                }
                onClick={() => handleStartChat(pathname, navigate)}
              >
                {t("translate.startChat")}
              </CommonButton>
            )}
            {id && (
              <CommonButton
                variant="secondary"
                startIcon={<RefreshIcon />}
                onClick={onOpen}
              >
                {t("translate.viewHistory")}
              </CommonButton>
            )}
            <ChatHistoryDrawer
              isOpen={isOpen}
              onClose={onClose}
              chatbotId={chatType} // pass the chatbotId dynamically
              mode={legalChatMode}
              projectId={getProjectId()}
            />
          </Flex>
        </HStack>
        <PanelGroup direction="horizontal" style={{ flex: 1 }}>
          <Panel defaultSize={50} minSize={20} maxSize={80}>
            <Box height={"100%"} bgColor={"white"}>
              <DrawingPdfViewer
                pdfUrl={documentData?.signedUrl}
                payload={payload}
              />
            </Box>
          </Panel>

          <PanelResizeHandle
            style={{
              width: "8px",
              backgroundColor: "#CDD6DC",
              cursor: "col-resize",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
            }}
          >
            <Box
              width="12px"
              height="40px"
              display="flex"
              alignItems="center"
              justifyContent="center"
              _hover={{
                backgroundColor: "#A0AEC0",
              }}
              transition="background-color 0.2s"
            >
              <Box
                display="grid"
                gridTemplateColumns="repeat(2, 1fr)"
                gridTemplateRows="repeat(3, 1fr)"
                gap="2px"
                width="8px"
                height="12px"
              >
                {Array.from({ length: 6 }, (_, i) => (
                  <Box
                    key={i}
                    width="2px"
                    height="2px"
                    backgroundColor="#718096"
                    borderRadius="1px"
                  />
                ))}
              </Box>
            </Box>
          </PanelResizeHandle>

          <Panel defaultSize={50} minSize={20} maxSize={80}>
            <Box height={"100%"} width={"100%"}>
              {renderMainLayout()}
            </Box>
          </Panel>
        </PanelGroup>
      </VStack>
    );
  };

  const renderContent = () => {
    if (isDrawingAnalysis) {
      return renderDrawingAnalysisLayout();
    }
    return renderMainLayout();
  };

  return renderContent();
};
